import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { useAgent } from '../../contexts/AgentContext';

const CALL_REASONS = [
  { label: 'תקלת ליקוט', value: 'Collection Error' },
  { label: 'תקלת הפצה', value: 'Distribution Error' },
];

const SUB_REASONS = {
  'Collection Error': ['חוסר במשלוח', 'עודף במשלוח', 'לוקט פריט שגוי'],
  'Distribution Error': ['יעד שגוי לאספקה', 'עיכוב במשלוח / מועד אספקה', 'הזמנה נשלחה ללא תיאום'],
};

// Israeli business days: Sun–Thu. Weekend = Fri (5) + Sat (6).
function businessDaysSince(deliveryDateStr) {
  if (!deliveryDateStr) return 0;
  const start = new Date(deliveryDateStr);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (today <= start) return 0;

  let count = 0;
  const current = new Date(start);
  while (current < today) {
    current.setDate(current.getDate() + 1);
    const day = current.getDay();
    if (day !== 5 && day !== 6) count++;
  }
  return count;
}

const AgentComplaintForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const { instance, accounts } = useMsal();
  const { agentCode, agentName } = useAgent();

  const orderNumber = searchParams.get('orderNumber') || '';
  const deliveryDate = searchParams.get('deliveryDate') || '';
  const order = state?.order;

  const [reporterName, setReporterName] = useState('');
  const [reporterRole, setReporterRole] = useState('');
  const [callReason, setCallReason] = useState('');
  const [subReason, setSubReason] = useState('');
  const [caseDescription, setCaseDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successId, setSuccessId] = useState(null);

  const businessDays = useMemo(() => businessDaysSince(deliveryDate), [deliveryDate]);
  const isExpired = businessDays > 3;

  const handleCallReasonChange = (e) => {
    setCallReason(e.target.value);
    setSubReason('');
  };

  const submitForm = async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const result = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      const payload = {
        agentCode,
        agentName,
        reporterName,
        reporterRole,
        order: {
          ORDNAME: orderNumber,
          CUSTNAME: order?.CUSTNAME || '',
          CDES: order?.CDES || '',
          DELIVERY_DATE: deliveryDate,
          REFERENCE: order?.REFERENCE || null,
        },
        complaint: {
          CALL_REASON: callReason,
          SUB_REASON: subReason,
          DESCRIPTION: caseDescription,
        },
      };
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/agent-complaints/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${result.accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error(`שגיאה בשליחת הטופס (${res.status})`);
      const data = await res.json();
      setSuccessId(data?.complaint_reference || data?.complaintId || data?.id || '—');
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitForm();
  };

  if (successId) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 10, sm: 11 }, direction: 'ltr' }}>
        <Paper
          elevation={0}
          sx={{
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: 2,
            p: { xs: 3, sm: 5 },
            textAlign: 'center',
            maxWidth: 480,
            mx: 'auto',
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            התלונה נשלחה בהצלחה
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 0.5 }}>
            מספר קריאה
          </Typography>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#0563B0', mb: 3 }}>
            {successId}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/agent-complaints')}
            sx={{ backgroundColor: '#0563B0', '&:hover': { backgroundColor: '#0454a0' } }}
          >
            חזרה לרשימה
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 10, sm: 11 }, direction: 'ltr' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1 }}>
        <IconButton onClick={() => navigate('/agent-complaints')} size="small">
          <ArrowForwardIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold" sx={{ color: '#0563B0' }}>
          פתיחת תלונה
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: 2,
          p: { xs: 2, sm: 3 }
        }}
      >
        {isExpired && (
          <Alert severity="error" sx={{ mb: 3 }}>
            לא ניתן לפתוח תלונה — עברו יותר מ-3 ימי עסקים מתאריך האספקה ({businessDays} ימי עסקים חלפו)
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="שם המדווח"
              value={reporterName}
              onChange={(e) => setReporterName(e.target.value)}
              fullWidth
              required
              size="small"
            />

            <TextField
              label="תפקיד המדווח"
              value={reporterRole}
              onChange={(e) => setReporterRole(e.target.value)}
              fullWidth
              required
              size="small"
            />

            <TextField
              label="מספר הזמנה"
              value={orderNumber}
              InputProps={{ readOnly: true }}
              fullWidth
              size="small"
            />

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                סוג תלונה
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                תלונה לוגיסטית
              </Typography>
            </Box>

            <FormControl fullWidth size="small" required>
              <InputLabel>סיבת קריאה</InputLabel>
              <Select value={callReason} onChange={handleCallReasonChange} label="סיבת קריאה">
                {CALL_REASONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" required disabled={!callReason}>
              <InputLabel>תת-סיבה</InputLabel>
              <Select
                value={subReason}
                onChange={(e) => setSubReason(e.target.value)}
                label="תת-סיבה"
              >
                {(SUB_REASONS[callReason] || []).map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="תיאור הפנייה"
              value={caseDescription}
              onChange={(e) => setCaseDescription(e.target.value)}
              multiline
              rows={4}
              fullWidth
              required
              size="small"
            />

            {submitError && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={submitForm}>
                    נסה שוב
                  </Button>
                }
              >
                {submitError}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={isExpired || submitting || !reporterName.trim() || !reporterRole.trim() || !callReason || !subReason || !caseDescription.trim()}
              sx={{
                backgroundColor: '#0563B0',
                '&:hover': { backgroundColor: '#0454a0' },
                alignSelf: 'flex-start',
                minWidth: 120,
              }}
            >
              {submitting ? <CircularProgress size={20} color="inherit" /> : 'שליחה'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default AgentComplaintForm;
